import Toast from 'react-native-toast-message'
import { getCurrentOrgID } from './organization';
import ErrorHandler from '../utils/ErrorHandler';
import { supabase } from './client';
import { insertActivity } from './activity';
import { buildUpdateDetails } from '../utils/buildActivityDetails';

/**
 * Helper to log custom-field / section activity (fire-and-forget at call sites).
 * Sections are treated as CustomField-type activity per spec.
 * @param {string} verb - 'create'|'update'|'delete'
 * @param {string} fieldID
 * @param {string} fieldName - human-readable field/section name
 * @param {string} [details] - optional human-readable description of what changed
 */
const logCustomFieldActivity = async (verb, fieldID, fieldName, details) => {
    await insertActivity({
        subjectType: 'CustomField',
        subjectID: fieldID,
        subjectName: fieldName,
        verb,
        details,
    });
};

// Render a field's options for activity details only when they are scalar
// (strings/numbers). Returns null for object-shaped options so we never leak
// raw objects into the human-readable feed.
const formatOptions = (opts) => {
    if (!Array.isArray(opts)) return null;
    const scalars = opts.filter((o) => typeof o === 'string' || typeof o === 'number');
    return scalars.length ? scalars.join(', ') : null;
};

const getCustomFieldSections = async (module) => {
    const orgID = await getCurrentOrgID();

    const { data, error } = await supabase
        .from("CustomFieldSections")
        .select("*, CustomFields(*)")
        .eq("organizationID", orgID)
        .eq("type", module)
        .order('position', { ascending: true })
        .order('position', { referencedTable: 'CustomFields', ascending: true });

    if (!error && data) {
        data.forEach(section => {
            if (section.CustomFields) {
                section.CustomFields = section.CustomFields.filter(field => !field.isDeleted);
            }
        });
    }

    if (error) {
        ErrorHandler(Error(error.message), { query: "getCustomFieldSections", orgID, module, ...error }, `Something went wrong fetching the custom field sections`)
        return [];
    }

    return data;
}

const getCustomFieldSection = async (id) => {
    const orgID = await getCurrentOrgID();

    const { data, error } = await supabase
        .from("CustomFieldSections")
        .select("*, Fields:CustomFields(*)")
        .eq("organizationID", orgID)
        .eq("id", id)
        .maybeSingle();

    if (!error && data && data.Fields) {
        data.Fields = data.Fields.filter(field => !field.isDeleted);
    }

    if (error) {
        ErrorHandler(Error(error.message), { query: "getCustomFieldSection", orgID, id, ...error }, `Something went wrong fetching the custom field section`)
        return [];
    }

    return data;
}

const getCustomFields = async (module, smallGroupTypeID = null) => {
    const orgID = await getCurrentOrgID();
    if (!orgID) return [];

    let query = supabase
        .from("CustomFields")
        .select("id, name, type, options, position, isProtected, required, module, sectionID, Section:CustomFieldSections(*)")
        .eq("organizationID", orgID)
        .eq("module", module)
        .eq("isDeleted", false);

    if (smallGroupTypeID) {
        query = query
            .in("id", supabase.from("SmallGroupTypeCustomFields").select("customFieldID").eq("smallGroupTypeID", smallGroupTypeID));
    }

    const { data, error } = await query;

    if (error) {
        ErrorHandler(Error(error.message), { query: "getCustomFields", orgID, module, smallGroupTypeID, ...error }, `Something went wrong fetching the custom fields`)
        return [];
    }

    return data.sort((f1, f2) => f1.Section?.position - f2.Section?.position || f1.position - f2.position || f1.name.localeCompare(f2.name));
}

const getCustomField = async (id) => {
    const orgID = await getCurrentOrgID();

    const { data, error } = await supabase
        .from("CustomFields")
        .select("id, name, position, type, options, module, required, sectionID, Section:CustomFieldSections(*)")
        .eq("organizationID", orgID)
        .eq("id", id)
        .eq("isDeleted", false)
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { query: "getCustomField", orgID, id, ...error }, `Something went wrong fetching the custom field`)
        return null;
    }

    return data;
}

const saveCustomField = async ({ details, originalValues }) => {
    const orgID = await getCurrentOrgID();

    let field = {
        id: details.id,
        name: details.name?.trim(),
        type: details.type,
        module: details.module,
        sectionID: details.sectionID,
        options: details.options || [],
        organizationID: orgID
    }

    if (details.position !== undefined) field.position = details.position;
    if (details.required !== undefined) field.required = details.required;
    if (details.isProtected !== undefined) field.isProtected = details.isProtected;

    if (!field.id) {
        return createCustomField(field, details.smallGroupTypeID);
    } else {
        return updateCustomField(field, originalValues);
    }
}

const createCustomField = async (field, smallGroupTypeID = null) => {
    const { error, data } = await supabase.from("CustomFields").insert(field).select().single();

    if (error) {
        if (error.message.includes("duplicate key value violates unique constraint")) {
            Toast.show({
                type: 'error',
                text2: `A field with the name "${field.name}" already exists for this module! Try using a different name.`
            });
        } else {
            ErrorHandler(Error(error.message), { mutation: "createCustomField", input: field, ...error }, `Something went wrong creating that field`)
        }
        throw Error()
    }

    // If linking to a SmallGroupType, create the join row
    if (smallGroupTypeID && data?.id) {
        const { error: linkError } = await supabase.from("SmallGroupTypeCustomFields").insert({
            smallGroupTypeID,
            customFieldID: data.id,
            position: field.position || 1000
        });

        if (linkError) {
            ErrorHandler(Error(linkError.message), { mutation: "linkCustomFieldToSmallGroupType", input: { smallGroupTypeID, customFieldID: data.id }, ...linkError }, `Field created but failed to link to Small Group Type`);
        }
    }

    Toast.delayShow({
        type: 'success',
        text2: `New field ${field.name} has been added successfully!`
    });

    logCustomFieldActivity('create', data.id, data.name).catch(() => {});

    return data;
}

const updateCustomField = async (field, originalValues = null) => {
    const { error, data } = await supabase.from("CustomFields").update(field).eq("id", field.id).select().single();

    if (error) {
        if (error.message.includes("duplicate key value violates unique constraint")) {
            Toast.show({
                type: 'error',
                text2: `A field with the name "${field.name}" already exists for this module! Try using a different name.`
            });
        } else if (error.code === "PGRST116") {
            Toast.show({
                type: 'error',
                text2: `That field could not be found. It may have been deleted.`
            });
        } else {
            ErrorHandler(Error(error.message), { mutation: "updateCustomField", input: field, ...error }, `Something went wrong updating that field`)
        }

        throw Error()
    }

    Toast.delayShow({
        type: 'success',
        text2: `Field was successfully updated!`
    });

    const details = originalValues ? buildUpdateDetails(originalValues, field, {
        name: { label: 'Name' },
        type: { label: 'Type' },
        required: { label: 'Required' },
        options: { label: 'Options', format: formatOptions },
    }) : null;
    logCustomFieldActivity('update', data.id, data.name, details).catch(() => {});

    return data;
}

const deleteCustomField = async (id) => {
    const { data: existing } = await supabase.from("CustomFields").select('name').eq("id", id).maybeSingle();

    const { error } = await supabase.from("CustomFields").update({ isDeleted: true }).eq("id", id);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "deleteCustomField", id: id, ...error }, `Something went wrong deleting that field`)
        throw Error()
    }

    Toast.show({
        type: 'success',
        text2: `The field was deleted successfully`
    });

    logCustomFieldActivity('delete', id, existing?.name).catch(() => {});
}

const saveCustomFieldSection = async (details) => {
    const orgID = await getCurrentOrgID();

    let section = {
        id: details.id,
        name: details.name,
        position: details.position !== undefined ? details.position : 1000,
        type: details.type,
        organizationID: orgID
    }

    if (!section.id) {
        return createCustomFieldSection(section, details.Fields);
    } else {
        return updateCustomFieldSection(section, details.Fields);
    }
}

const createCustomFieldSection = async (section, fields = []) => {
    const { data, error } = await supabase.from("CustomFieldSections").insert(section).select().single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "createCustomFieldSection", input: section, ...error }, `Something went wrong creating that section`)
        throw Error()
    }

    if (fields && fields.length > 0) {
        for (const field of fields) {
            const { error: fieldError } = await supabase.from("CustomFields").update({ sectionID: data.id, position: field.position }).eq('id', field.id);

            if (fieldError) {
                ErrorHandler(Error(fieldError.message), { mutation: "createCustomFieldSection", input: section, ...fieldError }, `Something went wrong updating the fields for that new section`)
                throw Error()
            }
        }
    }

    Toast.show({
        type: 'success',
        text2: `New section ${section.name} has been added successfully!`
    });

    logCustomFieldActivity('create', data.id, data.name).catch(() => {});

    return data;
}

const updateCustomFieldSection = async (section, fields = []) => {
    const { error, data } = await supabase.from("CustomFieldSections").update(section).eq("id", section.id).select().single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "updateCustomFieldSection", input: section, ...error }, `Something went wrong updating that section`)
        throw Error()
    }

    if (fields && fields.length > 0) {
        for (const field of fields) {
            const { error: fieldError } = await supabase.from("CustomFields").update({ sectionID: data.id, position: field.position }).eq('id', field.id);

            if (fieldError) {
                ErrorHandler(Error(fieldError.message), { mutation: "updateCustomFieldSection", input: section, ...fieldError }, `Something went wrong updating the fields for that section`)
                throw Error()
            }
        }
    }

    Toast.show({ type: 'success', text2: `Section was successfully updated!` });

    const details = buildUpdateDetails(null, section, {
        name: { label: 'Name' },
        position: { label: 'Position' },
    });
    logCustomFieldActivity('update', data.id, data.name, details).catch(() => {});

    return data;
}

const deleteCustomFieldSection = async (sectionID) => {
    const { data: existing } = await supabase.from("CustomFieldSections").select('name').eq("id", sectionID).maybeSingle();

    const { error } = await supabase.from("CustomFieldSections").delete().eq("id", sectionID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "deleteCustomFieldSection", input: sectionID, ...error }, `Something went wrong deleting that section`)
        throw Error()
    }

    Toast.show({
        type: 'success',
        text2: `The section was deleted successfully`
    });

    logCustomFieldActivity('delete', sectionID, existing?.name).catch(() => {});
}

const attachCustomFieldToSmallGroupType = async ({ customFieldID, smallGroupTypeID, position = 1000 }) => {
    const { error, data } = await supabase.from("SmallGroupTypeCustomFields").upsert({
        customFieldID,
        smallGroupTypeID,
        position
    }, { onConflict: 'smallGroupTypeID,customFieldID' }).select().single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "attachCustomFieldToSmallGroupType", input: { customFieldID, smallGroupTypeID }, ...error }, `Something went wrong attaching the custom field`)
        throw Error()
    }

    return data;
}

const detachCustomFieldFromSmallGroupType = async ({ customFieldID, smallGroupTypeID }) => {
    const { error } = await supabase.from("SmallGroupTypeCustomFields").delete()
        .eq("customFieldID", customFieldID)
        .eq("smallGroupTypeID", smallGroupTypeID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "detachCustomFieldFromSmallGroupType", input: { customFieldID, smallGroupTypeID }, ...error }, `Something went wrong detaching the custom field`)
        throw Error()
    }
}

export {
    getCustomFieldSections,
    getCustomFieldSection,
    getCustomFields,
    getCustomField,
    saveCustomField,
    deleteCustomField,
    saveCustomFieldSection,
    deleteCustomFieldSection,
    attachCustomFieldToSmallGroupType,
    detachCustomFieldFromSmallGroupType
}
